import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { FetchCurrentShiftQuery } from '../api/shifts/shift';
import { ShiftState, useShiftStore } from './shift-store';
import { useAuthStore } from '@/store/authStore';

/** Initialises a broadcast channel scoped to the current user + tenant */
export const useShiftBroadcastChannel = () => {
  const userId = useAuthStore((s) => s.user?.id);
  const tenantId = useAuthStore((s) => s.tenant?.tenantId);
  const channelKey = `shift:${userId ?? 'none'}:${tenantId ?? 'none'}`;

  const channel = useMemo(() => new BroadcastChannel(channelKey), [channelKey]);

  const sendMessage = useCallback(
    (event: ShiftChannelMessage) => {
      channel.postMessage(event);
    },
    [channel.postMessage]
  );

  return {
    channel,
    sendMessage,
  };
};

export type ShiftChannelMessage =
  | { type: 'user.activity'; time: Date }
  | { type: 'shift.start'; state: ShiftState }
  | { type: 'shift.end'; state: ShiftState }
  | { type: 'user.idle'; state: ShiftState }
  | { type: 'user.inactive'; state: ShiftState }
  | { type: 'break.start'; state: ShiftState }
  | { type: 'break.end'; state: ShiftState };

/** Listens to updates from other tabs and windows to help keep shift state in sync */
const useShiftBroadcastListener = () => {
  const queryClient = useQueryClient();
  const channel = useShiftBroadcastChannel();

  useEffect(() => {
    const invalidate = () => {
      console.debug('Invalidating current shift query via broadcast channel.');

      queryClient.invalidateQueries({
        queryKey: FetchCurrentShiftQuery().queryKey,
      });
    };

    const listener = (event: MessageEvent<ShiftChannelMessage>) => {
      const { data } = event;
      console.debug('Received message from shift channel', data);

      switch (data.type) {
        case 'shift.start': {
          invalidate();
          useShiftStore.setState(data.state);
          break;
        }
        case 'shift.end': {
          invalidate();
          useShiftStore.setState(data.state);
          break;
        }
        case 'break.start': {
          invalidate();
          useShiftStore.setState(data.state);
          break;
        }
        case 'break.end': {
          invalidate();
          useShiftStore.setState(data.state);
          break;
        }
        case 'user.activity': {
          useShiftStore.setState({ lastActiveAt: data.time });
          break;
        }
        case 'user.idle': {
          useShiftStore.setState(data.state);
          break;
        }
        case 'user.inactive': {
          useShiftStore.setState(data.state);
          break;
        }
      }
    };

    channel.channel.addEventListener('message', listener);

    return () => {
      channel.channel.removeEventListener('message', listener);
    };
  }, [queryClient]);
};

export const ShiftBroadcastListener = () => {
  useShiftBroadcastListener();
  return null;
};
